import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Crown, Lock, Sparkles, X, Check, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";
import { openStripeLink } from "../lib/stripe";

const PACK_META = {
  "deep-talk": { emoji: "💬", name: "Deep Talk", tagline: "For when you're feeling real." },
  "after-dark": { emoji: "🌙", name: "After Dark", tagline: "Candles out. Phones down. 18+." },
  "spicy-mode": { emoji: "🌶️", name: "Spicy Mode", tagline: "Turn up the heat. Zero filter." },
};

export default function PaywallSheet() {
  const { paywall, closePaywall } = useBilling();
  const open = !!paywall;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="paywall-overlay"
          onClick={closePaywall}
        >
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background: "rgba(5,3,14,0.70)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
            }}
          />
          <motion.div
            initial={{ y: "100%", opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: "100%", opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 26, stiffness: 300, mass: 0.8 }}
            className="relative z-10 w-full sm:max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="paywall-sheet"
          >
            <PaywallContent reason={paywall.reason} packId={paywall.packId} onClose={closePaywall} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PaywallContent({ reason, packId, onClose }) {
  const { user } = useAuth();
  const { refreshEntitlements, refreshUsage, intro, startOrFetchIntro, catalog: ctxCatalog } = useBilling();
  const [catalog, setCatalog] = useState(ctxCatalog || null);
  const [selected, setSelected] = useState("bundle_all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!ctxCatalog) {
      api.billingCatalog().then((c) => { if (alive) setCatalog(c); }).catch(() => {});
    } else {
      setCatalog(ctxCatalog);
    }
    startOrFetchIntro();
    return () => { alive = false; };
  }, [startOrFetchIntro, ctxCatalog]);

  useEffect(() => {
    refreshEntitlements();
    refreshUsage();
  }, [refreshEntitlements, refreshUsage]);

  // Auto-preselect the intro offer when eligible + this paywall is spicy-relevant
  const introEligible = !!intro?.eligible;
  const introRelevant = introEligible && (packId === "spicy-mode" || reason === "teaser");
  useEffect(() => {
    if (introRelevant) setSelected("intro_spicy");
  }, [introRelevant]);

  const headline = (() => {
    if (reason === "pack_locked" && packId) {
      const m = PACK_META[packId];
      return { title: `Unlock ${m?.name || "this pack"} ${m?.emoji || "🔒"}`, sub: m?.tagline || "Unlock to start playing." };
    }
    if (reason === "daily_cap")
      return {
        title: "You've hit today's free limit 🔥",
        sub: "Want to keep going? $4.99 unlocks everything forever.",
      };
    if (reason === "video") return { title: "Video responses are Premium 🎥", sub: "Unlock everything to record videos." };
    if (reason === "ai_pack") return { title: "Personal AI packs are Premium ✨", sub: "Unlock everything to generate your own packs." };
    if (reason === "teaser") return { title: "One taste of 🌶️ Spicy Mode", sub: "Unlock the whole deck — no filter." };
    return { title: "Unlock the full VibeCheck 🔥", sub: "All paid packs · Spicy · Deep Talk · After Dark · more." };
  })();

  const startCheckout = async (sku) => {
    const introLink = catalog?.intro?.payment_link;
    const bundleLink = catalog?.bundle?.payment_link;
    const link = sku === "intro_spicy" ? introLink : sku === "bundle_all" ? bundleLink : null;
    if (!link) {
      toast.error("Checkout link unavailable. Try again in a moment.");
      return;
    }
    setLoading(true);
    const ok = await openStripeLink(link, { user });
    setLoading(false);
    if (ok) {
      toast.success("Opened Stripe in a new tab — finish your purchase 💫");
      onClose();
    } else {
      toast.error("Couldn't open the checkout. Pop-up blocker?");
    }
  };

  return (
    <div
      className="bg-[#0F0F14]/95 border border-white/10 sm:rounded-3xl rounded-t-3xl p-6 sm:p-7 text-white relative overflow-hidden"
      style={{
        boxShadow: "0 -30px 100px rgba(255,77,141,0.25), 0 0 0 1px rgba(255,255,255,0.04)",
      }}
    >
      {/* Ambient energy */}
      <PaywallAmbient />

      <div className="relative">
        <button
          onClick={onClose}
          className="absolute -top-1 -right-1 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition"
          data-testid="paywall-close"
          aria-label="Close paywall"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] flex items-center justify-center shadow-[0_0_30px_rgba(255,77,141,0.5)]">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-[#C9C7D9] uppercase tracking-[0.3em]">VibeCheck Premium</p>
            <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight">{headline.title}</h3>
          </div>
        </div>
        <p className="text-sm text-[#C9C7D9] mb-5">{headline.sub}</p>

        {/* Teaser — blurred spicy card */}
        {reason === "teaser" && <TeaserCard />}

        {/* Two payment options: Spicy intro + Unlock-all bundle */}
        <div className="space-y-2.5 mb-4">
          <PlanCard
            sku="bundle_all"
            label="Unlock all packs"
            price={`$${catalog?.bundle?.amount?.toFixed(2) ?? "4.99"}`}
            sub="All paid packs · forever · best value"
            badge="Best value"
            selected={selected === "bundle_all"}
            onClick={() => setSelected("bundle_all")}
          />
          {introEligible && (
            <PlanCard
              sku="intro_spicy"
              label="Spicy Mode · 24hr deal"
              price={`$${catalog?.intro?.amount?.toFixed(2) ?? "0.99"}`}
              sub={`Was $1.99 · just spicy${packId === "spicy-mode" ? "" : " · this one only"}`}
              selected={selected === "intro_spicy"}
              onClick={() => setSelected("intro_spicy")}
            />
          )}
        </div>

        {/* Feature list */}
        <ul className="text-xs text-[#C9C7D9] space-y-1.5 mb-5">
          <FeatureRow>All 8 paid packs · Spicy · Deep Talk · After Dark · Lowkey Toxic · Red Flag Radar & more</FeatureRow>
          <FeatureRow>Future packs included — yours forever</FeatureRow>
          <FeatureRow>One-time payment · no subscription</FeatureRow>
        </ul>

        <ShimmerCTA
          onClick={() => startCheckout(selected)}
          loading={loading}
          label={
            selected === "intro_spicy"
              ? "Unlock 🌶️ for $0.99"
              : "Unlock everything · $4.99"
          }
        />

        <p className="text-center text-[10px] text-[#C9C7D9]/70 mt-3 uppercase tracking-[0.3em] font-semibold">
          Secure checkout by Stripe · One-time
        </p>
        <p
          className="text-center text-[10px] text-[#9B98AE] mt-2 uppercase tracking-[0.3em] font-bold"
          data-testid="paywall-age-disclaimer"
        >
          18+ · Adult content ·{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>{" "}
          ·{" "}
          <a href="/refund-policy" target="_blank" rel="noopener noreferrer" className="underline">Refunds</a>
        </p>
      </div>
    </div>
  );
}

function PlanCard({ sku, label, price, sub, badge, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`paywall-plan-${sku}`}
      className={`w-full flex items-center justify-between p-4 rounded-2xl border text-left transition-all ${
        selected
          ? "bg-gradient-to-br from-[#FF4D8D]/15 to-[#7B61FF]/15 border-[#FF4D8D]/60 shadow-[0_0_30px_rgba(255,77,141,0.25)]"
          : "bg-white/[0.03] border-white/10 hover:border-white/20"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            selected ? "border-[#FF4D8D] bg-[#FF4D8D]" : "border-white/30"
          }`}
        >
          {selected && <Check className="w-3 h-3 text-white" strokeWidth={4} />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-bold text-white text-sm">{label}</p>
            {badge && (
              <span className="text-[9px] font-bold bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                {badge}
              </span>
            )}
          </div>
          <p className="text-[11px] text-[#C9C7D9]">{sub}</p>
        </div>
      </div>
      <p className="font-display-tight font-bold text-lg">{price}</p>
    </button>
  );
}

function FeatureRow({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="w-3.5 h-3.5 text-[#00E5FF] mt-0.5 flex-shrink-0" strokeWidth={3} />
      <span>{children}</span>
    </li>
  );
}

function ShimmerCTA({ onClick, loading, label }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={loading}
      animate={{ scale: [1, 1.03, 1] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      whileTap={{ scale: 0.96 }}
      data-testid="paywall-cta"
      className="relative w-full py-4 rounded-full font-bold text-white text-base overflow-hidden disabled:opacity-70"
      style={{
        background: "linear-gradient(90deg, #FF4D8D 0%, #7B61FF 50%, #FF4D8D 100%)",
        backgroundSize: "200% 100%",
        boxShadow: "0 10px 40px rgba(255,77,141,0.5), 0 0 20px rgba(123,97,255,0.4)",
      }}
    >
      <motion.span
        className="absolute inset-0"
        animate={{ backgroundPositionX: ["0%", "200%"] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
          backgroundSize: "40% 100%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <span className="relative inline-flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
        {loading ? "Opening checkout…" : label}
      </span>
    </motion.button>
  );
}

function PaywallAmbient() {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-24 -left-10 w-64 h-64 rounded-full blur-3xl"
        animate={{ x: [0, 40, 0], y: [0, 20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        style={{ background: "radial-gradient(circle, rgba(255,77,141,0.45) 0%, transparent 70%)" }}
      />
      <motion.div
        className="absolute -bottom-24 -right-10 w-72 h-72 rounded-full blur-3xl"
        animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        style={{ background: "radial-gradient(circle, rgba(123,97,255,0.45) 0%, transparent 70%)" }}
      />
      {[...Array(8)].map((_, i) => (
        <motion.span
          key={i}
          className="absolute w-1 h-1 rounded-full bg-white/60"
          initial={{ opacity: 0, y: 0 }}
          animate={{
            y: [0, -60, 0],
            opacity: [0, 0.8, 0],
          }}
          transition={{ duration: 3 + i * 0.3, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
          style={{ left: `${10 + i * 11}%`, bottom: `${5 + (i % 3) * 20}%` }}
        />
      ))}
    </div>
  );
}

function TeaserCard() {
  return (
    <div className="relative mb-4 rounded-2xl overflow-hidden border border-[#FF4D8D]/30">
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0.6, 1, 0.6], scale: [1, 1.02, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background: "radial-gradient(circle at 30% 30%, rgba(255,77,141,0.25), transparent 70%)",
        }}
      />
      <div className="relative p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FF4D8D]/20 flex items-center justify-center">
          <Lock className="w-4 h-4 text-[#FF4D8D]" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF4D8D] font-bold">Preview</p>
          <p
            className="font-bold text-white text-sm sm:text-base select-none tracking-tight"
            style={{ filter: "blur(6px)" }}
            data-testid="teaser-blurred"
          >
            What's a fantasy you've been too shy to share with me until now?
          </p>
        </div>
        <Sparkles className="w-5 h-5 text-[#FF4D8D]" />
      </div>
    </div>
  );
}

function useCountdown(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  const ms = Math.max(0, end - now);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return { ms, h, m, s, expired: ms <= 0 };
}

function IntroOfferCard({ expiresAt, selected, onClick }) {
  const cd = useCountdown(expiresAt);
  if (!cd || cd.expired) return null;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid="paywall-plan-intro_spicy"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      className={`relative w-full text-left mb-3 overflow-hidden rounded-2xl border-2 ${
        selected ? "border-[#FF4D8D]" : "border-[#FF4D8D]/60"
      }`}
      style={{
        background:
          "linear-gradient(135deg, rgba(255,77,141,0.24), rgba(123,97,255,0.24)), rgba(15,15,20,0.9)",
        boxShadow: selected
          ? "0 0 50px rgba(255,77,141,0.55), 0 0 0 1px rgba(255,77,141,0.5)"
          : "0 0 40px rgba(255,77,141,0.35)",
      }}
    >
      {/* Shimmer sweep */}
      <motion.span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{ backgroundPositionX: ["0%", "200%"] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: "linear" }}
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)",
          backgroundSize: "45% 100%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="relative p-4 flex items-center gap-3">
        <motion.span
          animate={{ scale: [1, 1.15, 1], rotate: [0, 8, -8, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="text-3xl"
        >
          🌶️
        </motion.span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-[9px] font-bold bg-[#FF4D8D] text-white px-2 py-0.5 rounded-full uppercase tracking-[0.2em]">
              First-open deal
            </span>
            <span className="text-[10px] font-bold text-[#FF4D8D] uppercase tracking-[0.2em]">
              24h only
            </span>
          </div>
          <p className="font-bold text-white text-base leading-tight tracking-tight">
            Spicy Mode · intro
          </p>
          <p className="text-[11px] text-[#C9C7D9] flex items-center gap-1.5 mt-0.5" data-testid="intro-countdown">
            Ends in{" "}
            <span className="font-mono font-bold text-[#FF4D8D] tabular-nums">
              {String(cd.h).padStart(2, "0")}:{String(cd.m).padStart(2, "0")}:{String(cd.s).padStart(2, "0")}
            </span>
          </p>
        </div>
        <div className="text-right">
          <p className="font-display-tight font-bold text-2xl text-white">$0.99</p>
          <p className="text-[10px] text-[#C9C7D9] line-through">$1.99</p>
        </div>
      </div>
    </motion.button>
  );
}

